import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useConfirm } from '@/hooks/useConfirm';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { User, Mail, Phone, Lock, Flame, Save, Eye, EyeOff, CheckCircle, Shield, Key, Copy, RefreshCw, Bell, AtSign, AlertTriangle, Crown, Zap, Trash2, Star, ChevronRight, RotateCcw, Heart, X, Target, CreditCard, BarChart3, Compass, LogOut, Pencil, Calendar } from 'lucide-react';

/* ── XP Level System ── */
const XP_LEVELS = [
  { title: 'Budget Padawan',      minXp: 0,    emoji: '🌱' },
  { title: 'Bachat Warrior',      minXp: 100,  emoji: '⚔️' },
  { title: 'Paisa Samajhdar',     minXp: 300,  emoji: '🧠' },
  { title: 'Nivesh Ninja',        minXp: 600,  emoji: '🥷' },
  { title: 'Dhan Shilpi',         minXp: 1000, emoji: '🏗️' },
  { title: 'Paisa Pandit',        minXp: 1500, emoji: '📚' },
  { title: 'Artha Guru',          minXp: 2200, emoji: '🧘' },
  { title: 'Lakshmi ka Laadla',   minXp: 3000, emoji: '✨' },
  { title: 'Chanakya Shishya',    minXp: 4000, emoji: '🦉' },
  { title: 'Arthashastra Master', minXp: 5500, emoji: '👑' },
];

function getLevel(xp) {
  let level = XP_LEVELS[0];
  for (const l of XP_LEVELS) {
    if (xp >= l.minXp) level = l;
    else break;
  }
  const idx = XP_LEVELS.indexOf(level);
  const next = XP_LEVELS[idx + 1] || null;
  const progressToNext = next
    ? ((xp - level.minXp) / (next.minXp - level.minXp)) * 100
    : 100;
  return { ...level, idx, next, progressToNext: Math.min(progressToNext, 100), xp };
}

const ProfilePage = () => {
  const { user, token, refreshUser, logout } = useAuth();
  const confirm = useConfirm();
  const [form, setForm] = useState({ name: '', phone: '', salary_day: '' });
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [streak, setStreak] = useState(0);
  const [lastActivity, setLastActivity] = useState('');
  const [profileLocked, setProfileLocked] = useState(false);
  const [lockToggling, setLockToggling] = useState(false);
  const [pdfPassword, setPdfPassword] = useState('');
  const [generatingPin, setGeneratingPin] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({
    whatsapp_enabled: true, email_enabled: false, notify_via_chat: true,
    notify_emi: true, notify_subscriptions: true, notify_birthdays: true,
    notify_budget_summary: true, notify_savings_goals: true,
    notify_hand_loans: true, notify_salary: true, notify_when_to_buy: true,
    reminder_days_before: 3,
  });
  const [savingNotif, setSavingNotif] = useState(false);
  const [deleteStep, setDeleteStep]   = useState(0); // 0=idle, 1=confirm, 2=deleting
  const [deleteInput, setDeleteInput] = useState('');
  const [cancelStep, setCancelStep]   = useState(0); // 0=idle, 1=confirm
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [pwModalOpen, setPwModalOpen]   = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        phone: user.phone || '',
        salary_day: user.salary_day != null ? String(user.salary_day) : '',
      });
      setStreak(user.streak || 0);
      setLastActivity(user.last_activity_date || '');
      setProfileLocked(user.profile_locked || false);
      setPdfPassword(user.pdf_password || '');
    }
  }, [user]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/notifications/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setNotifPrefs(p => ({ ...p, ...r.data })))
      .catch(() => {});
  }, [token]);

  const handleSaveNotifPrefs = async () => {
    setSavingNotif(true);
    try {
      await axios.put(`${API}/notifications/preferences`, notifPrefs, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Notification preferences saved!');
    } catch { toast.error('Failed to save preferences'); }
    finally { setSavingNotif(false); }
  };

  const toggleNotif = (key) => setNotifPrefs(p => ({ ...p, [key]: !p[key] }));

  const handleToggleLock = async () => {
    setLockToggling(true);
    try {
      const r = await axios.post(`${API}/auth/toggle-profile-lock`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setProfileLocked(r.data.profile_locked);
      toast.success(r.data.profile_locked ? '🔒 Profile locked' : '🔓 Profile unlocked');
      refreshUser?.();
    } catch { toast.error('Failed to toggle lock'); }
    finally { setLockToggling(false); }
  };

  const handleGeneratePin = async () => {
    setGeneratingPin(true);
    try {
      const r = await axios.post(`${API}/auth/generate-pdf-password`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPdfPassword(r.data.pdf_password);
      toast.success('New PDF PIN generated!');
    } catch { toast.error('Failed to generate PIN'); }
    finally { setGeneratingPin(false); }
  };

  const handleSave = async () => {
    // Validate salary_day: if provided, must be integer 1..28
    let salaryDayVal = null;
    if (form.salary_day !== '' && form.salary_day != null) {
      const n = parseInt(form.salary_day, 10);
      if (Number.isNaN(n) || n < 1 || n > 28) {
        toast.error('Salary day must be a number between 1 and 28');
        return;
      }
      salaryDayVal = n;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        name: form.name,
        phone: form.phone,
        salary_day: salaryDayVal,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Profile updated!');
      refreshUser?.();
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSavingPw(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.newPw,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully!');
      setPwForm({ current: '', newPw: '', confirm: '' });
      setPwModalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPw(false);
    }
  };

  const handleCancelPro = async () => {
    try {
      await axios.post(`${API}/auth/toggle-pro`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Pro subscription cancelled. You now have the free plan.');
      refreshUser?.();
      setCancelStep(0);
    } catch { toast.error('Could not cancel subscription. Please contact support.'); }
  };

  const handleDeleteAccount = async () => {
    if (deleteInput.trim().toLowerCase() !== 'delete') {
      toast.error('Please type "delete" to confirm');
      return;
    }
    setDeleteStep(2);
    try {
      await axios.delete(`${API}/auth/account`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Account deleted. Goodbye!');
      logout?.();
    } catch {
      toast.error('Could not delete account. Please contact support@budgetmantra.in');
      setDeleteStep(1);
    }
  };

  /* ── Profile Completeness ── */
  const completeness = useMemo(() => {
    let pct = 0;
    if (user?.name) pct += 15;
    if (user?.email) pct += 15;
    if (user?.phone || form.phone) pct += 10;
    if (user?.dob) pct += 10;
    if (user?.avatar) pct += 10;
    try { if (localStorage.getItem('bm_life_profile')) pct += 20; } catch {}
    try { if (localStorage.getItem('bm_salary_profile')) pct += 20; } catch {}
    return pct;
  }, [user, form.phone]);

  const completenessColor = completeness < 50 ? 'bg-red-500' : completeness < 80 ? 'bg-amber-500' : 'bg-emerald-500';
  const completenessTextColor = completeness < 50 ? 'text-red-600' : completeness < 80 ? 'text-amber-600' : 'text-emerald-600';

  /* ── XP / Level ── */
  const levelInfo = useMemo(() => {
    let xp = 0;
    try { xp = parseInt(localStorage.getItem('bm_web_xp') || '0', 10) || 0; } catch {}
    return getLevel(xp);
  }, []);

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const streakLabel = streak === 0 ? 'No streak yet'
    : streak === 1 ? '1 day streak 🔥'
    : `${streak} day streak 🔥`;

  const TABS = [
    { id: 'profile', label: 'Profile' },
    { id: 'security', label: 'Security' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'danger', label: 'Account' },
  ];

  const handleSignOut = async () => {
    if (await confirm('Sign out of BudgetMantra?')) logout?.();
  };

  const handleClearCache = async () => {
    if (await confirm('Clear local app cache? Your account and server data will NOT be affected.')) {
      localStorage.clear();
      toast.success('Cache cleared. Reloading…');
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Page header */}
      <div className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-0">
          {/* Avatar + name row */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-orange-500 flex items-center justify-center text-white text-xl font-extrabold shrink-0">
              {initials}
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-stone-900 dark:text-stone-100">{user?.name || 'Account'}</h1>
              <p className="text-sm text-stone-500 dark:text-stone-400">{user?.email}</p>
            </div>
            {streak > 0 && (
              <div className="ml-auto flex items-center gap-1.5 bg-orange-50 dark:bg-orange-950/40 border border-orange-100 dark:border-orange-900 rounded-full px-3 py-1.5">
                <Flame size={13} className="text-orange-500" />
                <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">{streakLabel}</span>
              </div>
            )}
          </div>

          {/* Tab bar */}
          <div className="flex gap-0 border-b border-stone-200 dark:border-stone-800 -mb-px">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">

        {/* ── PROFILE TAB ── */}
        {activeTab === 'profile' && (
          <>
            {/* Personal info */}
            <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800">
              <div className="px-6 py-4 border-b border-stone-100 dark:border-stone-800">
                <h2 className="font-bold text-stone-800 dark:text-stone-100">Personal Information</h2>
                <p className="text-xs text-stone-400 mt-0.5">Update your name, phone, and salary cycle</p>
              </div>
              <div className="divide-y divide-stone-100 dark:divide-stone-800">
                {/* Full Name */}
                <div className="flex items-center gap-4 px-6 py-4">
                  <div className="w-40 shrink-0">
                    <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Full Name</p>
                  </div>
                  <Input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    className="flex-1 h-9 bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700 rounded-lg text-sm"
                  />
                  <Button onClick={handleSave} disabled={saving} size="sm"
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 h-9 text-xs font-bold shrink-0">
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
                {/* Phone */}
                <div className="flex items-center gap-4 px-6 py-4">
                  <div className="w-40 shrink-0">
                    <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Phone</p>
                  </div>
                  <Input
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    type="tel"
                    className="flex-1 h-9 bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700 rounded-lg text-sm"
                  />
                  <Button onClick={handleSave} disabled={saving} size="sm"
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 h-9 text-xs font-bold shrink-0">
                    Save
                  </Button>
                </div>
                {/* Salary Day */}
                <div className="flex items-center gap-4 px-6 py-4">
                  <div className="w-40 shrink-0">
                    <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Salary Day</p>
                    <p className="text-xs text-stone-400 mt-0.5">Day of month you get paid</p>
                  </div>
                  <Input
                    type="number" min={1} max={28}
                    value={form.salary_day}
                    onChange={e => setForm({ ...form, salary_day: e.target.value })}
                    placeholder="e.g. 27"
                    className="w-28 h-9 bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700 rounded-lg text-sm"
                  />
                  <Button onClick={handleSave} disabled={saving} size="sm"
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 h-9 text-xs font-bold shrink-0">
                    Save
                  </Button>
                </div>
                {/* Email (readonly) */}
                <div className="flex items-center gap-4 px-6 py-4">
                  <div className="w-40 shrink-0">
                    <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Email</p>
                  </div>
                  <p className="flex-1 text-sm text-stone-400">{user?.email}</p>
                  <span className="text-xs text-stone-400 shrink-0">Cannot be changed</span>
                </div>
              </div>
            </div>

            {/* Profile completeness */}
            <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 px-6 py-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-stone-800 dark:text-stone-100 flex items-center gap-2">
                  <CheckCircle size={16} className="text-orange-500" /> Profile Completeness
                </h2>
                <span className={`text-sm font-bold ${completenessTextColor}`}>{completeness}%</span>
              </div>
              <div className="w-full h-2 bg-stone-100 dark:bg-stone-800 rounded-full overflow-hidden mb-3">
                <div className={`h-full rounded-full transition-all duration-700 ${completenessColor}`} style={{ width: `${completeness}%` }} />
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { ok: !!user?.name, label: 'Name' },
                  { ok: !!user?.email, label: 'Email' },
                  { ok: !!(user?.phone || form.phone), label: 'Phone' },
                  { ok: !!user?.dob, label: 'DOB' },
                  { ok: !!(() => { try { return localStorage.getItem('bm_life_profile'); } catch { return null; } })(), label: 'Life Profile' },
                  { ok: !!(() => { try { return localStorage.getItem('bm_salary_profile'); } catch { return null; } })(), label: 'Salary Profile' },
                ].map(({ ok, label }) => (
                  <span key={label} className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${ok ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700 text-stone-400'}`}>
                    {ok ? <CheckCircle size={10} /> : <span className="w-2.5 h-2.5 rounded-full border border-current inline-block" />}
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Level */}
            <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 px-6 py-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-950/40 rounded-2xl flex items-center justify-center text-2xl shrink-0">
                  {levelInfo.emoji}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-stone-400 uppercase tracking-wider font-semibold">Level {levelInfo.idx + 1}</p>
                  <p className="font-bold text-stone-800 dark:text-stone-100">{levelInfo.title}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-1.5 bg-stone-100 dark:bg-stone-800 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${levelInfo.progressToNext}%` }} />
                    </div>
                    <span className="text-xs text-stone-400">{levelInfo.xp} XP</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Life Profile link */}
            <Link to="/life-profile" className="flex items-center justify-between bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 px-6 py-4 hover:border-orange-300 transition-colors group">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center">
                  <Heart size={17} className="text-rose-500" />
                </div>
                <div>
                  <p className="font-semibold text-stone-800 dark:text-stone-100 text-sm">Life Profile</p>
                  <p className="text-xs text-stone-400 mt-0.5">Family, stage, obligations</p>
                </div>
              </div>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-orange-500 transition-colors" />
            </Link>
          </>
        )}

        {/* ── SECURITY TAB ── */}
        {activeTab === 'security' && (
          <>
            {/* Change Password */}
            <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800">
              <div className="px-6 py-4 border-b border-stone-100 dark:border-stone-800">
                <h2 className="font-bold text-stone-800 dark:text-stone-100">Change Password</h2>
                <p className="text-xs text-stone-400 mt-0.5">Use a strong password of at least 8 characters</p>
              </div>
              <div className="px-6 py-5 space-y-4">
                {[
                  { label: 'Current Password', key: 'current', placeholder: 'Your current password' },
                  { label: 'New Password', key: 'newPw', placeholder: 'At least 8 characters' },
                  { label: 'Confirm New Password', key: 'confirm', placeholder: 'Re-enter new password' },
                ].map(({ label, key, placeholder }) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-48 shrink-0">
                      <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">{label}</p>
                    </div>
                    <div className="flex-1 relative">
                      <Input
                        type={showPw ? 'text' : 'password'}
                        placeholder={placeholder}
                        value={pwForm[key]}
                        onChange={e => setPwForm({ ...pwForm, [key]: e.target.value })}
                        className="h-9 bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700 rounded-lg text-sm pr-9"
                      />
                      {key === 'current' && (
                        <button type="button" onClick={() => setShowPw(!showPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-orange-500">
                          {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <Button onClick={handlePasswordChange} disabled={savingPw || !pwForm.current || !pwForm.newPw || !pwForm.confirm}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg px-6 h-9 text-sm font-bold disabled:opacity-50">
                    {savingPw ? 'Updating…' : 'Update Password'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Profile Lock + PDF PIN */}
            <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 divide-y divide-stone-100 dark:divide-stone-800">
              <div className="px-6 py-4">
                <h2 className="font-bold text-stone-800 dark:text-stone-100">Privacy & Access</h2>
              </div>
              {/* Profile Lock */}
              <div className="flex items-center justify-between gap-4 px-6 py-4">
                <div>
                  <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Profile Lock</p>
                  <p className="text-xs text-stone-400 mt-0.5">{profileLocked ? 'Locked — no profile edits allowed' : 'Unlocked — profile edits enabled'}</p>
                </div>
                <button onClick={handleToggleLock} disabled={lockToggling}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${profileLocked ? 'bg-rose-500' : 'bg-stone-300'} disabled:opacity-50`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${profileLocked ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              {/* PDF PIN */}
              <div className="flex items-start justify-between gap-4 px-6 py-4">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-stone-700 dark:text-stone-300 flex items-center gap-1.5">
                    <Key size={13} className="text-orange-500" /> PDF Export PIN
                  </p>
                  <p className="text-xs text-stone-400 mt-0.5 leading-relaxed max-w-sm">
                    Printed on every exported PDF — only you know it. Keep it safe.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {pdfPassword ? (
                    <>
                      <span className="font-mono text-base font-bold text-stone-800 dark:text-stone-100 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 px-3 py-1.5 rounded-lg tracking-widest">
                        {pdfPassword}
                      </span>
                      <button onClick={() => { navigator.clipboard.writeText(pdfPassword); toast.success('PIN copied!'); }}
                        className="p-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-500 hover:text-orange-500 transition-colors">
                        <Copy size={13} />
                      </button>
                      <button onClick={handleGeneratePin} disabled={generatingPin}
                        className="p-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-500 hover:text-orange-500 transition-colors">
                        <RefreshCw size={13} className={generatingPin ? 'animate-spin' : ''} />
                      </button>
                    </>
                  ) : (
                    <button onClick={handleGeneratePin} disabled={generatingPin}
                      className="text-xs font-semibold text-orange-500 border border-orange-200 px-3 py-1.5 rounded-lg hover:bg-orange-50 transition-colors">
                      {generatingPin ? 'Generating…' : '+ Generate PIN'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── NOTIFICATIONS TAB ── */}
        {activeTab === 'notifications' && (
          <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 divide-y divide-stone-100 dark:divide-stone-800">
            <div className="px-6 py-4">
              <h2 className="font-bold text-stone-800 dark:text-stone-100">Notification Preferences</h2>
              <p className="text-xs text-stone-400 mt-0.5">Choose how and when BudgetMantra notifies you</p>
            </div>

            {/* Email toggle */}
            <div className="flex items-center justify-between gap-4 px-6 py-4">
              <div>
                <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Email Notifications</p>
                <p className="text-xs text-stone-400 mt-0.5">{user?.email}</p>
              </div>
              <button onClick={() => toggleNotif('email_enabled')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${notifPrefs.email_enabled ? 'bg-orange-500' : 'bg-stone-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${notifPrefs.email_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Chanakya chat toggle */}
            <div className="flex items-center justify-between gap-4 px-6 py-4">
              <div>
                <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Chanakya Chat Reminders</p>
                <p className="text-xs text-stone-400 mt-0.5">Reminders appear in your chat feed</p>
              </div>
              <button onClick={() => toggleNotif('notify_via_chat')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${notifPrefs.notify_via_chat ? 'bg-orange-500' : 'bg-stone-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${notifPrefs.notify_via_chat ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Reminder days */}
            <div className="flex items-center justify-between gap-4 px-6 py-4">
              <div>
                <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Reminder Lead Time</p>
                <p className="text-xs text-stone-400 mt-0.5">Days before EMI / subscription due</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setNotifPrefs(p => ({ ...p, reminder_days_before: Math.max(1, p.reminder_days_before - 1) }))}
                  className="w-7 h-7 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 font-bold flex items-center justify-center hover:border-orange-300">−</button>
                <span className="w-6 text-center font-bold text-stone-800 dark:text-stone-100 text-sm">{notifPrefs.reminder_days_before}</span>
                <button onClick={() => setNotifPrefs(p => ({ ...p, reminder_days_before: Math.min(14, p.reminder_days_before + 1) }))}
                  className="w-7 h-7 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 font-bold flex items-center justify-center hover:border-orange-300">+</button>
                <span className="text-xs text-stone-400 ml-1">days</span>
              </div>
            </div>

            {/* Feature toggles */}
            {[
              { key: 'notify_emi',            label: 'EMI & Loan due dates' },
              { key: 'notify_subscriptions',  label: 'Subscription renewals' },
              { key: 'notify_birthdays',      label: 'Birthdays & anniversaries' },
              { key: 'notify_savings_goals',  label: 'Savings goal deadlines' },
              { key: 'notify_when_to_buy',    label: 'Smart Buy item ready' },
              { key: 'notify_hand_loans',     label: 'Hand loan due dates' },
              { key: 'notify_salary',         label: 'Salary / paycheck day' },
              { key: 'notify_budget_summary', label: 'Monthly budget summary' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between gap-4 px-6 py-3.5">
                <p className="text-sm text-stone-700 dark:text-stone-300">{label}</p>
                <button onClick={() => toggleNotif(key)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${notifPrefs[key] ? 'bg-orange-500' : 'bg-stone-300'}`}>
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${notifPrefs[key] ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}

            <div className="px-6 py-4">
              <Button onClick={handleSaveNotifPrefs} disabled={savingNotif}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-10 px-6 text-sm font-bold">
                {savingNotif ? 'Saving…' : 'Save Preferences'}
              </Button>
            </div>
          </div>
        )}

        {/* ── DANGER ZONE TAB ── */}
        {activeTab === 'danger' && (
          <div className="space-y-4">

            {/* ── Account actions (reversible) ── */}
            <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 divide-y divide-stone-100 dark:divide-stone-800">
              <div className="px-6 py-4">
                <h2 className="font-bold text-stone-700 dark:text-stone-200 text-sm">Account Actions</h2>
                <p className="text-xs text-stone-400 mt-0.5">These actions are safe and reversible.</p>
              </div>

              {/* Sign Out */}
              <div className="flex items-center justify-between gap-4 px-6 py-4">
                <div>
                  <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Sign Out</p>
                  <p className="text-xs text-stone-400 mt-0.5">Sign out of your account on this device. You can sign back in anytime.</p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="text-xs font-semibold text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-700 px-4 py-2 rounded-xl hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors shrink-0">
                  Sign Out
                </button>
              </div>

              {/* Reset cache */}
              <div className="flex items-center justify-between gap-4 px-6 py-4">
                <div>
                  <p className="text-sm font-semibold text-stone-700 dark:text-stone-300">Clear App Cache</p>
                  <p className="text-xs text-stone-400 mt-0.5">Clears locally cached data. Your account and server data are not affected.</p>
                </div>
                <button
                  onClick={handleClearCache}
                  className="text-xs font-semibold text-amber-700 border border-amber-200 dark:border-amber-800 px-4 py-2 rounded-xl hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors shrink-0">
                  Clear Cache
                </button>
              </div>
            </div>

            {/* ── True danger zone ── */}
            <div className="bg-rose-50 dark:bg-rose-950/20 rounded-2xl border border-rose-200 dark:border-rose-900">
              <div className="px-6 py-4 border-b border-rose-100 dark:border-rose-900/50 flex items-center gap-2">
                <AlertTriangle size={15} className="text-rose-500 shrink-0" />
                <div>
                  <h2 className="font-bold text-rose-600 dark:text-rose-400 text-sm">Danger Zone</h2>
                  <p className="text-xs text-rose-400 dark:text-rose-500 mt-0.5">These actions are permanent and cannot be undone.</p>
                </div>
              </div>

              <div className="px-6 py-5">
                {deleteStep === 0 ? (
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-bold text-rose-700 dark:text-rose-400">Delete Account</p>
                      <p className="text-xs text-rose-500/80 dark:text-rose-500 mt-0.5">Permanently removes all your data, budgets, transactions, and goals. Cannot be undone.</p>
                    </div>
                    <button
                      onClick={() => setDeleteStep(1)}
                      className="text-xs font-bold text-rose-600 border-2 border-rose-300 dark:border-rose-700 px-4 py-2 rounded-xl hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors shrink-0">
                      Delete Account
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 bg-rose-100 dark:bg-rose-900/30 rounded-xl p-4">
                      <AlertTriangle size={16} className="text-rose-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-rose-700 dark:text-rose-300">This will permanently delete your entire account</p>
                        <p className="text-xs text-rose-500 mt-1">All budgets, transactions, goals, EMIs, investments, and chat history will be erased. This cannot be reversed.</p>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-rose-700 dark:text-rose-400">
                        Type <span className="font-black">DELETE</span> to confirm
                      </label>
                      <input
                        value={deleteInput}
                        onChange={e => setDeleteInput(e.target.value)}
                        placeholder="Type DELETE here"
                        className="w-full h-10 px-3 rounded-xl border-2 border-rose-200 dark:border-rose-800 bg-white dark:bg-stone-900 text-sm text-stone-800 dark:text-stone-100 focus:outline-none focus:border-rose-500"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => { setDeleteStep(0); setDeleteInput(''); }}
                        className="flex-1 py-2.5 rounded-xl border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 text-sm font-semibold hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors">
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteInput.toLowerCase() !== 'delete' || deleteStep === 2}
                        className="flex-1 py-2.5 rounded-xl bg-rose-500 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold flex items-center justify-center gap-1.5 transition-colors">
                        <Trash2 size={13} />
                        {deleteStep === 2 ? 'Deleting…' : 'Delete my account'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProfilePage;
